import { ICurrentUser } from '@dadohr/shared/domainTypes/currentUser';
import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  ExperienceAnchorType,
  IExperienceTemplate
} from '@dadohr/shared/domainTypes/experience';
import {
  IReportDrilldown,
  IReportingQuery,
  IReportingQueryDrilldown,
  IReportOverview,
  IReportTasks
} from '@dadohr/shared/domainTypes/reports';
import { decodeJsonParam } from '@dadohr/shared/domainTypes/routes';
import { FS } from '@dadohr/shared/versions';
import { mapValues, sortBy } from 'lodash';
import moment from 'moment-timezone';
import { usePromise } from '../../../hooks/usePromise';
import {
  useExperienceTemplateById,
  useReportableExperienceTemplatesByOrgId
} from '../../../services/experiences/template';
import {
  combineLoadingValues,
  LoadingValue,
  LoadingValueExtended,
  refreshTimestamp,
  store,
  useMappedLoadingValue
} from '../../../services/firebase';
import { callFirebaseFunction } from '../../../services/firebaseFunctions';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../../../services/firecache/documentListener';
import { getOrgIdOrFreeTierSampleContentOrgId } from '../../../services/organizationTier';
import { hasCurrentUserExperienceLevelScopes } from '../../../services/permissions';
import { fromMoment, ONE_MINUTE } from '../../../services/time';
import { toTimeSavedStatsDoc } from './documents';
import { REPORT_OVERVIEW_SAMPLE_DATA } from './fixtures';

export const useDefaultReportingQueryParams = (
  currentUser: ICurrentUser
): LoadingValue<IReportingQuery> => {
  const orgId = getOrgIdOrFreeTierSampleContentOrgId(currentUser.org);
  return useMappedLoadingValue(
    useReportableExperienceTemplatesByOrgId(orgId),
    (tpls) => {
      const accessibleTpls = tpls.filter((tpl) =>
        hasCurrentUserExperienceLevelScopes(currentUser, tpl.id, ['view'])
      );
      const sorted = sortBy(
        accessibleTpls,
        (e) => -e.data.updatedAt.toMillis()
      );
      const firstTpl = sorted[0];
      const hasMilestoneAnchor =
        firstTpl?.data.anchor.type === ExperienceAnchorType.EMPLOYEE_DATE;
      const end = moment()
        .add(hasMilestoneAnchor ? 30 : 0, 'days')
        .endOf('day');
      const start = end.clone().subtract(90, 'days').startOf('day');
      return {
        orgId,
        tplIds: sorted.slice(0, 1).map((x) => x.id),
        timeRange: {
          start: fromMoment(start),
          end: fromMoment(end)
        },
        filters: EMPTY_ARR
      };
    }
  );
};

export const parseReportingQueryParams = (
  q: string
): IReportingQuery | null => {
  if (!q) {
    return null;
  }
  const rawQ = decodeJsonParam<IReportingQuery>(q);
  return {
    ...rawQ,
    timeRange: {
      start: refreshTimestamp(rawQ.timeRange.start),
      end: refreshTimestamp(rawQ.timeRange.end)
    },
    filters: rawQ.filters || EMPTY_ARR
  };
};

export const useReportsOverviewDataExampleData = (
  q: IReportingQuery
): LoadingValueExtended<IReportOverview> => {
  // mimic loading state
  // return [undefined, true, boolean];
  return usePromise(async () => REPORT_OVERVIEW_SAMPLE_DATA(q), [q]);
};

// const INSTANCE_CACHE: { [key: string]: Doc<IExperience>[] } = {};
// const toInstanceCacheKey = (args: {
//   tplId: string;
//   timeRange: {
//     start: Timestamp;
//     end: Timestamp;
//   };
// }) => {
//   const key = JSON.stringify({
//     tplId: args.tplId,
//     start: args.timeRange.start.toMillis(),
//     end: args.timeRange.end.toMillis()
//   });
//   return key;
// };

// const mapSnapshotWithCache = (
//   args: {
//     tplId: string;
//     timeRange: {
//       start: Timestamp;
//       end: Timestamp;
//     };
//   },
//   s: firebase.firestore.QuerySnapshot
// ) => {
//   // avoid cached results from firestore so that the screen doesn't flicker,
//   // as it's very likely that only partial results will be cached by it
//   const cacheKey = toInstanceCacheKey(args);
//   console.log('SNAP', s.metadata.fromCache);
//   if (s.metadata.fromCache) {
//     if (INSTANCE_CACHE[cacheKey]) {
//       return INSTANCE_CACHE[cacheKey];
//     }
//     throw WAIT_FOR_NEXT_VALUE;
//   }
//   const insts = s.docs.map(toExperienceDoc);
//   INSTANCE_CACHE[cacheKey] = insts;
//   return insts;
// };

const OVERVIEW_CACHE: {
  [key: string]: Promise<IReportOverview>;
} = {};
const getReportsOverviewDataViaCfCached = async (
  q: IReportingQuery
): Promise<IReportOverview> => {
  console.time('getReportsOverviewDataViaCfCached');
  const cacheKey = JSON.stringify(q);
  if (!OVERVIEW_CACHE[cacheKey]) {
    const nextData = callFirebaseFunction<IReportOverview>(
      'reports-getOverviewData',
      q
    ).then((d) => {
      d.q.timeRange.start = refreshTimestamp(d.q.timeRange.start);
      d.q.timeRange.end = refreshTimestamp(d.q.timeRange.end);

      return d;
    });
    OVERVIEW_CACHE[cacheKey] = nextData;
  }
  const data = await OVERVIEW_CACHE[cacheKey];
  console.timeEnd('getReportsOverviewDataViaCfCached');
  return data;
};

export const useReportsOverviewDataViaCf = (
  q: IReportingQuery
): LoadingValueExtended<IReportOverview> => {
  return usePromise(() => getReportsOverviewDataViaCfCached(q), [q]);
};

// export const getReportsTasksData = async (
//   q: IReportingQuery
// ): Promise<IReportTasks> => {
//   return REPORT_TASKS_SAMPLE_DATA(q);
// };

// export const useReportsTasksData = (
//   q: IReportingQuery
// ): LoadingValueExtended<IReportTasks> => {
//   // mimic loading state
//   // return [undefined, true, boolean];
//   return usePromise(async () => getReportsTasksData(q), [q]);
// };

const TASKS_CACHE: {
  [key: string]: Promise<IReportTasks>;
} = {};
const getReportsTasksDataViaCfCached = async (
  q: IReportingQuery
): Promise<IReportTasks> => {
  console.time('getReportsTasksDataViaCfCached');
  const cacheKey = JSON.stringify(q);
  if (!TASKS_CACHE[cacheKey]) {
    const nextData = callFirebaseFunction<IReportTasks>(
      'reports-getTasksData',
      q
    ).then((d) => {
      d.q.timeRange.start = refreshTimestamp(d.q.timeRange.start);
      d.q.timeRange.end = refreshTimestamp(d.q.timeRange.end);
      d.data.byCohort.cohortsByWeek.forEach((c) => {
        c.anchor.date = refreshTimestamp(c.anchor.date);
        c.tasksByTemplateId = mapValues(c.tasksByTemplateId, (v) => {
          v.normalTaskDates.start = refreshTimestamp(v.normalTaskDates.start);
          v.normalTaskDates.end = refreshTimestamp(v.normalTaskDates.end);
          return v;
        });
      });

      return d;
    });
    TASKS_CACHE[cacheKey] = nextData;
  }
  const data = await TASKS_CACHE[cacheKey];
  console.timeEnd('getReportsTasksDataViaCfCached');
  return data;
};

export const useReportsTasksDataViaCf = (
  q: IReportingQuery
): LoadingValue<IReportTasks & { tpl: Doc<IExperienceTemplate> }> => {
  // TODO currently only using one template at all times!
  const tplId = q.tplIds[0];
  return useMappedLoadingValue(
    combineLoadingValues(
      useExperienceTemplateById(tplId),
      usePromise(() => getReportsTasksDataViaCfCached(q), [q])
    ),
    ([tpl, data]) => {
      if (!tpl || !data) {
        throw new Error('data not found');
      }
      return {
        ...data,
        tpl
      };
    }
  );
};

const timeSavedStatsForOrgStore = createDocumentListenerGetter(
  (orgId) => store().collection(FS.timeSavedStats).doc(orgId),
  toTimeSavedStatsDoc
);
const useTimeSavedStatsForOrg = (orgId: string) => {
  return useDocumentListener(timeSavedStatsForOrgStore(orgId));
};

export const useTimeSavedForOrg = (orgId: string) => {
  return useMappedLoadingValue(useTimeSavedStatsForOrg(orgId), (d) => {
    if (!d) {
      return 0;
    }
    return (
      d.data.itemsRun.tasks * ONE_MINUTE * 5 +
      d.data.itemsRun.messages * ONE_MINUTE * 5 +
      d.data.itemsRun.automationActions * ONE_MINUTE * 5
    );
  });
};

const DRILLDOWN_CACHE: {
  [key: string]: Promise<IReportDrilldown>;
} = {};

const getReportsDrilldownViaCfCached = async (
  q: IReportingQueryDrilldown
): Promise<IReportDrilldown> => {
  console.time('getReportsDrilldownViaCfCached');
  const cacheKey = JSON.stringify(q);
  if (!DRILLDOWN_CACHE[cacheKey]) {
    const nextData = callFirebaseFunction<IReportDrilldown>(
      'reports-getDrilldownData',
      { q }
    );
    DRILLDOWN_CACHE[cacheKey] = nextData;
  }
  const data = await DRILLDOWN_CACHE[cacheKey];
  console.timeEnd('getReportsViaCfCached');
  return data;
};

export const useReportsDrilldownDataByInstance = (
  q: IReportingQueryDrilldown
) => {
  return usePromise(async () => {
    return getReportsDrilldownViaCfCached(q);
  }, [q]);
};
